// Puente con el sistema host (Sistemas Patagonia) cuando la app corre embebida en un iframe.
// Protocolo postMessage:
//   - La app, al cargar, emite  { type: 'embed:ready' } al parent.
//   - El host responde con      { type: 'embed:init', token, bffUrl, theme?, locale?, company? }
//   - El host puede enviar       { type: 'embed:token', token }  (refresh)
//   - La app emite               { type: 'embed:resize', height }  y eventos de negocio.
// En modo standalone (sin parent) toma token/bffUrl de la query string para desarrollo.

export interface EmbedState {
  token: string | null;
  bffUrl: string;
  company: string | null;
  theme?: Record<string, string>;
  locale: string;
  ready: boolean;
}

type Listener = (state: EmbedState) => void;

const state: EmbedState = {
  token: null,
  bffUrl: '',
  company: null,
  theme: undefined,
  locale: 'es-AR',
  ready: false,
};

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l({ ...state });
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener({ ...state });
  return () => listeners.delete(listener);
}

export function getState(): EmbedState {
  return { ...state };
}

function applyInit(payload: Partial<EmbedState>) {
  if (payload.token !== undefined) state.token = payload.token;
  if (payload.bffUrl) state.bffUrl = payload.bffUrl;
  if (payload.company !== undefined) state.company = payload.company;
  if (payload.theme) state.theme = payload.theme;
  if (payload.locale) state.locale = payload.locale;
  state.ready = !!state.token && !!state.bffUrl;
  emit();
}

export function postToHost(message: Record<string, unknown>) {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
}

// La app pide al host un token nuevo (silent refresh).
export function requestTokenRefresh() {
  postToHost({ type: 'embed:token:refresh-request' });
}

export function reportEvent(event: string, data?: unknown) {
  postToHost({ type: 'embed:event', event, data });
}

export function reportResize(height: number) {
  postToHost({ type: 'embed:resize', height });
}

// Inicializa el puente: escucha mensajes del host o, en standalone, lee la query string.
export function initHostBridge() {
  window.addEventListener('message', (e: MessageEvent) => {
    const msg = e.data;
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'embed:init') {
      applyInit({
        token: msg.token,
        bffUrl: msg.bffUrl,
        company: msg.company ?? null,
        theme: msg.theme,
        locale: msg.locale,
      });
    } else if (msg.type === 'embed:token') {
      applyInit({ token: msg.token });
    }
  });

  // Avisar al host que estamos listos para recibir el init.
  postToHost({ type: 'embed:ready' });

  // Fallback standalone (desarrollo): ?token=...&bffUrl=...&company=...
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const bffUrl = params.get('bffUrl');
  if (token || bffUrl) {
    applyInit({
      token: token ?? undefined,
      bffUrl: bffUrl ?? state.bffUrl,
      company: params.get('company') ?? undefined,
    });
  }
}
